import {useCallback, useContext, useMemo, useState} from "react";
import {apartmentSearchAPI} from "../common/api/apartmentSearchAPI";
import {API_APARTMENT_SEARCH_RESORTS_SEARCH} from "../common/api/apiConstants";
import {createURLQuery} from "../common/api/apiUtil";
import {getUniqueResortTags} from "../selectors/resorts";
import {LocaleContext} from "../store/providers/LocaleProvider";
import {useLazyQuery, gql} from "@apollo/client";

const RESORTS_BY_TAG_QUERY = gql`
query searchResortsByTags($tags: [String]!, $locale: String!) {
    resortCollection(where: { resortPositioning: { identifier_in: $tags}, isBookable: true }, locale: $locale) {
        items {
            resortName,
            hapimagResortId
            resortPositioningCollection {
                items {
                    identifier
                }
            }
        }
    }
}
`;

const RESORT_POSITIONING_BY_RESORT = gql`
query useResortPositioningEntriesByResortsIds($resortIds: [Int]!) {
    resortCollection(where: { hapimagResortId_in: $resortIds, isBookable: true }) {
        items {
            hapimagResortId
            resortPositioningCollection {
                items {
                    identifier
                }
            }
        }
    }
}
`;

export const useResortsSearch = () => {
    const {localeUppercaseUnderscore, localeUppercase} = useContext(LocaleContext)[0];
    const [searchByTags] = useLazyQuery(RESORTS_BY_TAG_QUERY, {errorPolicy: "all", fetchPolicy: "no-cache"});
    const [getPositioning] = useLazyQuery(RESORT_POSITIONING_BY_RESORT, {errorPolicy: "all", fetchPolicy: "no-cache"});

    const [searchResortList, setSearchResortList] = useState([]);
    const [alternativeResortList, setAlternativeResortList] = useState([]);
    const [filteredResortsList, setFilteredResortsList] = useState();
    const [resortTags, setResortTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [maxGuestsExceeded, setMaxGuestsExceeded] = useState(false);

    // HELPER FUNCTIONS
    // Helper function which fetches the resorts with availability
    const fetchResorts = async (params) => {
        const res = await apartmentSearchAPI.get(API_APARTMENT_SEARCH_RESORTS_SEARCH + createURLQuery(params));
        return res?.data?.resorts;
    };

    // Helper functions which finds most common tags of resort list
    const getCommonTagsSorted = async (resortIds) => {
        const tagCount = {};

        const resortTags = await getTagsByResort(resortIds);

        // Count occurrences of each tag
        for (let i = 0; i < resortTags.length; i++) {
            const tags = resortTags[i].tags;
            for (let j = 0; j < tags.length; j++) {
                const tag = tags[j];
                tagCount[tag] = (tagCount[tag] || 0) + 1;
            }
        }

        // Sort tags by count in descending order
        const sortedTags = Object.keys(tagCount).sort((a, b) => tagCount[b] - tagCount[a]);

        // Extract sorted tags into an array
        const commonTagsSorted = sortedTags.map(tag => ({ tag, count: tagCount[tag] }));

        return resortTags?.length === 1 ? commonTagsSorted : commonTagsSorted?.filter(t => t.count > 1);
    };

    // Helper function which retrives the resort based on a list of tags sorted by relevance
    const getResortIdsByTags = useCallback(async (tags) => {
        const { data } = await searchByTags({variables: {tags, locale: localeUppercase}});

        const resortData = data?.resortCollection?.items;

        const resortTags = [];
        for (const resort of resortData) {
            resortTags?.push({
                id: resort?.hapimagResortId,
                tags: resort?.resortPositioningCollection?.items?.map(rp => rp?.identifier)
            });
        }

        // Sort resorts based on the most common tags
        const result = resortTags?.sort((obj1, obj2) => {
            const tags1 = obj1.tags;
            const tags2 = obj2.tags;

            for (let tag of tags) {
                const index1 = tags1.indexOf(tag);
                const index2 = tags2.indexOf(tag);

                if (index1 === -1 && index2 !== -1) {
                    return 1;
                } else if (index1 !== -1 && index2 === -1) {
                    return -1;
                } else if (index1 !== -1 && index2 !== -1) {
                    if (index1 < index2) {return -1;}
                    if (index1 > index2) {return 1;}
                }
            }

            // If both objects have the same tags in the same order, maintain original order
            return 0;

        });

        return result?.map(r => r.id);
    }, [localeUppercase]);

    // Helper functions which retrieves tags for resorts
    const getTagsByResort = useCallback(async (resortIds) => {
        const {data, error} = await getPositioning({variables: {resortIds}});
        if (error) {
            throw error;
        }

        return data?.resortCollection?.items?.map((r) => {
            return {
                id: r?.hapimagResortId,
                tags: r?.resortPositioningCollection?.items?.map(rp => rp.identifier)
            };
        });
    }, [getPositioning]);

    // Helper function which finds the alternative resorts
    const getAlternativeResorts = useCallback(async (searchParams) => {
        const resortIds = searchParams?.resort_id?.map(r => Number(r));
        const tags = await getCommonTagsSorted(resortIds);

        // Get resort IDs sorted by most common tags and exclude the ones selected by user
        const alternativeResortIds = (
            await getResortIdsByTags(tags?.map(t => t.tag))
        ).filter(r => !resortIds?.includes(r));

        if (!alternativeResortIds || !alternativeResortIds?.length) {
            // No alternatives found
            return [];
        }

        // Fetch the alternative resorts
        const resortsRes = await fetchResorts({
            ...searchParams,
            resort_id: alternativeResortIds
        });

        // Sort the response resort based on relevance
        const sorted = [];
        for (const id of alternativeResortIds) {
            const resort = resortsRes?.find(r => r.id === id);
            if (resort) {
                sorted?.push(resort);
            }
        }

        // If we have dates then filter the ones tha have availability, otherwise return everything
        return (searchParams?.date_to && searchParams.date_from) ? sorted?.filter(r => r.number_of_available_apartments) : sorted;
    }, [getResortIdsByTags, getTagsByResort]);

    // Helper filtering & sorting function
    const filterAndSortByRelevantTags = (list, tags) => {
        const filteredList = list
            .filter(item => item.resort_tags.some(tag => tags.includes(tag)))
            .sort((a, b) => {
                const aMatches = a.resort_tags.filter(tag => tags.includes(tag)).length;
                const bMatches = b.resort_tags.filter(tag => tags.includes(tag)).length;
                return bMatches - aMatches;
            });

        return filteredList;
    };

    // EXPOSED FUNCTIONS
    const applyFiltersTags = useCallback((tags) => {
        if (!tags?.length) {
            return setFilteredResortsList(undefined);
        }

        setFilteredResortsList({
            result: filterAndSortByRelevantTags(searchResortList, tags),
            alternatives: filterAndSortByRelevantTags(alternativeResortList, tags)
        });
    }, [searchResortList, alternativeResortList, resortTags]);

    const loadResorts = useCallback(async (params) => {
        setLoading(true);
        setError(null);
        setFilteredResortsList(null);
        setAlternativeResortList([]);

        let searchParams = {...params};
        if (!searchParams?.locale) {
            searchParams.locale = localeUppercaseUnderscore || "en_GB";
        }
        if (searchParams?.flexible_dates) {
            searchParams.duration = params.flexible_dates;
            delete searchParams.flexible_dates;
        }

        try {
            // Fetch selected resorts for user
            const resortsRes = await fetchResorts(searchParams);

            if (resortsRes?.length > 0) {
                const allMaxGuestsExceeded = resortsRes.every(resort => resort.max_guests_exceeded);
                setMaxGuestsExceeded(allMaxGuestsExceeded);
            } else {
                setMaxGuestsExceeded(false);
            }

            const validResorts = resortsRes?.filter(resort => !resort.max_guests_exceeded);
            const resorts = getUniqueResortTags(validResorts);

            // Construct unique resort tags
            const uniqueTags = [...new Set(resorts?.flatMap(resort => resort.resort_tags))];

            // Alternative resorts
            const alternativeResorts = await getAlternativeResorts(searchParams);
            const validAlternativeResorts = alternativeResorts.filter(resort => !resort.max_guests_exceeded);

            // Apply initial filtering if any
            if (searchParams?.highlights?.length) {
                setFilteredResortsList({
                    result: filterAndSortByRelevantTags(resorts, searchParams.highlights),
                    alternatives: filterAndSortByRelevantTags(validAlternativeResorts, searchParams.highlights)
                });
            }

            setSearchResortList(resorts);
            setAlternativeResortList(validAlternativeResorts);
            setResortTags(uniqueTags);
        } catch(err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [getAlternativeResorts, getCommonTagsSorted]);

    return useMemo(() => ({
        loadResorts,
        searchResortList: filteredResortsList
            ? filteredResortsList.result
            : searchResortList,
        alternativeResortList: filteredResortsList
            ? filteredResortsList.alternatives
            : alternativeResortList,
        initialList: searchResortList,
        resortTags,
        applyFiltersTags,
        loading,
        error,
        maxGuestsExceeded
    }), [
        loadResorts,
        searchResortList,
        alternativeResortList,
        resortTags,
        applyFiltersTags,
        loading,
        error,
        filteredResortsList
    ]);
};
